import { useEffect, useState } from 'react';
import s from './pay-card.module.scss'
import { Button } from '@shared/ui/button/button'
import { FormProvider, useForm } from 'react-hook-form'
import { classes } from '@shared/api/services'
import { useNavigate, useParams } from 'react-router-dom';
import { Input } from '@shared/ui/input/input'
import { queryClient } from '@shared/api/query-client'
import { useEnabledPromocode } from '@widgets/student/api/useEnabledPromocode'
import { toast } from 'react-toastify'
import { FetchingDiv } from '@shared/ui/fetching-div/fetching-div'
import { IconLoader, IconX } from '@tabler/icons-react/dist/cjs/tabler-icons-react'
import { findGroupNameByEng } from '@entities/profile/lib/timezone-picker/utils'
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji';
import { ConsentBlock, ConsentCaption } from '@shared/ui/consent-block/consent-block';
import { getNoun } from '@utils/lib/getNoun';
import { TRIAL_LESSON_PRICE, TRIAL_LESSON_DURATION } from '@shared/lib/trial';
import { SelectParticipant } from '@features/appointment/select-participant/select-participant';

export const PayCard = ({selectedTime, selectedDate, selectedInstrument, set_selectedTime, submitLabel, pack, economy, singleLessonPrice, onPayPack, packLoading}) => {
	const navigate = useNavigate()
	const {id} = useParams()
	const teacher = queryClient.getQueryData(['teacher', id])
	const isPackMode = !!pack
	const isTrial = !isPackMode && !!teacher?.trial_enabled
	const lessonDuration = isTrial ? TRIAL_LESSON_DURATION : Number(teacher?.grade?.duration) || 50
	
	const {data: profile} = queryClient.getQueryData(['profile'])
	const methods = useForm()
	const {handleSubmit} = methods

	useEffect(() => {
		methods.setValue('personal_data_consent', true)
	}, [methods])
	const [promocode, setPromocode] = useState(null)
	const [isSubmitting, setIsSubmitting] = useState(false)
	// Для кого бронируется занятие: null — владелец аккаунта, иначе id детского профиля
	const [participantId, setParticipantId] = useState(null)

	const {data: price, isFetching, error} = useEnabledPromocode({
		promocode,
		teacher_id: isPackMode ? undefined : teacher?.id
	})

	const promoInputValue = methods?.watch('promocode')
	const inputPromocode = promoInputValue?.trim().toUpperCase() || ''
	const submittedPromocode = promocode?.trim() || ''
	const promocodeUsed = !!price?.promocode_used
	// Идёт проверка последнего отправленного кода
	const isChecking = !!submittedPromocode && isFetching
	// Ответ по последнему отправленному коду получен, но код не применён
	const promocodeRejected = !!submittedPromocode && !isFetching && !promocodeUsed && submittedPromocode === inputPromocode

	// Автоприменение промокода с дебаунсом
	useEffect(() => {
		const value = promoInputValue?.trim()
		if (!value) {
			setPromocode(null)
			return
		}
		if (promocodeUsed) return
		const timer = setTimeout(() => setPromocode(value.toUpperCase()), 600)
		return () => clearTimeout(timer)
	}, [promoInputValue, promocodeUsed])

	const applyPromocode = () => {
		if (!inputPromocode || promocodeUsed) return
		setPromocode(inputPromocode)
	}

	const clearPromocode = () => {
		methods.setValue('promocode', '')
		setPromocode(null)
	}

	const handlePromoKeyPress = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			applyPromocode()
		}
	}

	const createClass = () => {
		setIsSubmitting(true)
		window.ym(96194074,'reachGoal','create-appointment-by-teacher-page')
		const requestData = {
			"teacher_id": teacher?.id,
			"time": selectedTime.length === 5 ? selectedTime : '0' + selectedTime,
			"date": selectedDate,
			"duration": "1h",
			"instrument_id": selectedInstrument?.id,
			"promocode": price?.promocode_used ? promocode : '',
			// Профиль участника занятия; для владельца аккаунта не передаём
			...(participantId ? { "profile_id": participantId } : {})
		}

		const refetchTeacher = async () => await queryClient.refetchQueries(['teacher', id])
		const refetchActivePacks = async () => await queryClient.refetchQueries({ queryKey: ['active-packs'] })
		// Списки занятий кэшируются до редиректа на оплату / перехода на
		// post-payment — сбрасываем их, чтобы новое занятие появилось сразу
		const invalidateLessonsLists = () => {
			queryClient.invalidateQueries({ queryKey: ['appointments'] })
			queryClient.invalidateQueries({ queryKey: ['upcoming-appointments'] })
			queryClient.invalidateQueries({ queryKey: ['history'] })
			queryClient.invalidateQueries({ queryKey: ['orders'] })
		}

		classes.createClass(requestData)
		.then(async res => {
			localStorage.setItem('personal_data_consent_given', 'true')
			await refetchActivePacks()
			invalidateLessonsLists()
			if(price?.outside_payment_available || (+price?.discounted_price === 0)){
				navigate('/post-payment/appointment-' + res?.data?.appointment?.id)
			}else{
				if(res?.data?.success === true){
					classes.payForAppointment(res?.data?.order?.id).then(payment => {
						if(payment?.data?.payment_url){
							// Спиннер крутится до перехода на страницу оплаты
							window.open(payment?.data?.payment_url, "_self")
						}else{
							setIsSubmitting(false)
							toast.error('Не удалось получить ссылку на оплату')
						}
					}).catch((err) => {
						refetchTeacher()
						toast.error(`${err?.response?.data?.message || 'Возникла ошибка'}`)
						set_selectedTime(null)
						setIsSubmitting(false)
						return err
					})
				}else{
					setIsSubmitting(false)
				}
			}
		}).catch((err) => {
			refetchTeacher()
			set_selectedTime(null)
			setIsSubmitting(false)
			return err
		})
	}

  const onSubmit = handleSubmit(() => {
		if (isPackMode) {
			onPayPack?.()
		} else {
			createClass()
		}
  })

	const is_discount = price?.price !== price?.discounted_price || price?.price === 0
	const is_free = +price?.discounted_price === 0
	const without_pay = !isPackMode && (price?.outside_payment_available || +price?.discounted_price === 0)
  const personalDataConsent = methods?.watch('personal_data_consent')

	const defaultSubmitLabel = isPackMode
		? "Перейти к оплате"
		: without_pay
		? "Забронировать занятие"
		: "Перейти к оплате"
	const primarySubmitLabel = submitLabel || defaultSubmitLabel

	return (
    <FormProvider {...methods}>
      <div className={s.paymentCard}>
        {isPackMode ? (
          <div className={s.infoWrapper}>
            <span className={s.cardTitle}>Оплата пакета</span>
            <div className={s.infoRow}>
              <span className={s.infoLabel}>Количество занятий</span>
              <span className={s.infoValue}>
                {pack?.lessons_count} {getNoun(pack?.lessons_count, "занятие", "занятия", "занятий")}
              </span>
            </div>
            {pack?.instrument?.name && (
              <div className={s.infoRow}>
                <span className={s.infoLabel}>Инструмент</span>
                <span className={s.infoValue}><TextWithEmoji text={pack?.instrument?.name}/></span>
              </div>
            )}
            {singleLessonPrice > 0 && (
              <div className={s.infoRow}>
                <span className={s.infoLabel}>Стоимость одного занятия</span>
                <span className={s.infoValue}>{singleLessonPrice.toLocaleString("ru-RU")} ₽</span>
              </div>
            )}
            {economy > 0 && (
              <div className={s.infoRow}>
                <span className={s.infoLabel}>Экономия</span>
                <span className={s.infoValue}>{economy.toLocaleString("ru-RU")} ₽</span>
              </div>
            )}
          </div>
        ) : (
          <div className={s.infoWrapper}>
            <span className={s.cardTitle}>{isTrial ? 'Пробное занятие' : price?.outside_payment_available ? 'Бронирование' : 'Оплата'}</span>
            <div className={s.participantRow}>
              <span className={s.infoLabel}>Кто занимается</span>
              <SelectParticipant value={participantId} onChange={setParticipantId} />
            </div>
            <div className={s.infoRow}>
              <span className={s.infoLabel}>Время начала занятия</span>
              <span className={s.infoValue}>
                {selectedTime} {findGroupNameByEng(profile?.timezone)}
              </span>
            </div>
            <div className={s.infoRow}>
              <span className={s.infoLabel}>Продолжительность занятия</span>
              <span className={s.infoValue}>{lessonDuration} минут</span>
            </div>
            <div className={s.infoRow}>
              <span className={s.infoLabel}>Инструмент</span>
              <span className={s.infoValue}><TextWithEmoji text={selectedInstrument?.name}/></span>
            </div>
          </div>
        )}
        {!isPackMode && !without_pay && !isTrial && (
          <div className={s.promocodeWrapper}>
            <span className={s.promocodeTitle}>Промокод</span>
            {promocodeUsed ? (
              <div className={s.appliedPromo}>
                <div className={s.appliedPromoInfo}>
                  <span className={s.appliedCode}>{promocode}</span>
                  {price?.reason && (
                    <span className={s.appliedReason}>{price?.reason}</span>
                  )}
                </div>
                <button
                  type="button"
                  className={s.appliedRemove}
                  onClick={clearPromocode}
                  aria-label="Убрать промокод"
                >
                  <IconX />
                </button>
              </div>
            ) : (
              <>
                <div className={s.promoRow}>
                  <Input
                    name={"promocode"}
                    className={s.input}
                    placeholder={"Ваш промокод"}
                    onKeyPress={handlePromoKeyPress}
                    rightIcon={isChecking ? <IconLoader className={s.loadingIcon} /> : null}
                  />
                  <Button
                    label="Применить"
                    size="small"
                    className={s.promoApplyBtn}
                    disabled={!inputPromocode || isChecking}
                    fetching={isChecking}
                    onClick={applyPromocode}
                  />
                </div>
                {promocodeRejected && (
                  <span className={s.errorText}>
                    {price?.reason || error?.response?.data?.message || 'Промокод не найден или не соблюдены условия'}
                  </span>
                )}
              </>
            )}
          </div>
        )}
        <div className={s.footerWrapper}>
          <FetchingDiv className={s.footer} isFetching={isFetching}>
            {isPackMode ? (
              <div className={s.infoWrapper}>
                <span className={s.infoTitle}>Итого</span>
                <div className={s.infoRow}>
                  <span className={s.infoLabel}>Итого к оплате</span>
                  <span className={s.infoValue}>
                    <span className={s.finalPrice}>
                      {pack?.price?.toLocaleString("ru-RU")} ₽
                    </span>
                  </span>
                </div>
              </div>
            ) : (
              !price?.outside_payment_available && 
                <div className={s.infoWrapper}>
                  <span className={s.infoTitle}>Итого</span>
                  {isTrial ? (
                    <>
                      <div className={s.infoRow}>
                        <span className={s.infoLabel}>Стоимость пробного занятия</span>
                        <span className={s.infoValue}>{TRIAL_LESSON_PRICE + " ₽"}</span>
                      </div>
                      <div className={s.infoRow}>
                        <span className={s.infoLabel}>Итого к оплате</span>
                        <span className={s.infoValue}>
                          <span className={s.finalPrice}>
                            {TRIAL_LESSON_PRICE + " ₽"}
                          </span>
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                  {is_free || <div className={s.infoRow}>
                    <span className={s.infoLabel}>Стоимость занятия</span>
                    <span className={s.infoValue}>{price?.price + " ₽"}</span>
                  </div>}
                  <div className={s.infoRow}>
                    <span className={s.infoLabel}>Скидка</span>
                    <span className={s.infoValue}>
                      {is_free ? (
                        <div className={s.trialBadge}>{price?.reason}</div>
                      ) : (
                        <span className={s.salePrice}>
                          {+price?.discounted_price - +price?.price + " ₽"}
                          {is_discount && <span className={s.saleReason}>{price?.reason}</span>}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className={s.infoRow}>
                    <span className={s.infoLabel}>Итого к оплате</span>
                    <span className={s.infoValue}>
                      <span className={s.finalPrice}>
                        {+price?.discounted_price + " ₽"}
                      </span>
                    </span>
                  </div>
                    </>
                  )}
                </div>
            )}
            <ConsentBlock className={s.consentBlock} showUserAgreementCaption={false}/>
            <Button
              label={primarySubmitLabel}
              onClick={onSubmit}
              className={s.btn}
              fetching={isPackMode ? packLoading : isSubmitting}
              disabled={
                isPackMode
                  ? !pack || packLoading || !personalDataConsent
                  : isSubmitting ||
                    !selectedTime ||
                    !selectedDate ||
                    !teacher ||
                    !selectedInstrument ||
                    !personalDataConsent
              }
            />
          </FetchingDiv>
          <ConsentCaption className={s.consentCaption}/>
        </div>
      </div>

    </FormProvider>
  );
}

